/**
 * @file
 * Tidies what the tool editors hold. Text that doesn't parse comes back as it
 * was, since formatting is a convenience and never a check.
 */

export type CodeFormatLanguage = "javascript" | "json";

/** JSON indented by two spaces, or the text itself when it isn't JSON. */
export function formatJson(text: string): string {
  if (text.trim() === "") return text;
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

/**
 * JavaScript as Prettier formats it by default, or the text itself when it
 * doesn't parse. Prettier is loaded on first use, in chunks of its own.
 */
export async function formatJavaScript(text: string): Promise<string> {
  if (text.trim() === "") return text;
  const [prettier, babel, estree] = await Promise.all([
    import("prettier/standalone"),
    import("prettier/plugins/babel"),
    import("prettier/plugins/estree"),
  ]);
  try {
    return await prettier.format(text, {
      parser: "babel",
      plugins: [babel, estree],
    });
  } catch {
    return text;
  }
}

export async function formatCode(
  text: string,
  language: CodeFormatLanguage,
): Promise<string> {
  switch (language) {
    case "javascript":
      return formatJavaScript(text);
    case "json":
      return formatJson(text);
  }
}
